import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../AppContext';
import SideHustleLayout from '../../components/SideHustleLayout';

interface BudgetItem {
  description: string;
  amount: number;
}

const BusinessBudgeting: React.FC = () => {
  const { currentUser } = useApp();
  const [income, setIncome] = useState<BudgetItem[]>([]);
  const [expenses, setExpenses] = useState<BudgetItem[]>([]);
  const [newIncome, setNewIncome] = useState({ description: '', amount: '' });
  const [newExpense, setNewExpense] = useState({ description: '', amount: '' });
  const [saving, setSaving] = useState(false);

  // Edit state for income
  const [editingIncomeIndex, setEditingIncomeIndex] = useState<number | null>(null);
  const [editIncome, setEditIncome] = useState({ description: '', amount: '' });

  // Edit state for expenses
  const [editingExpenseIndex, setEditingExpenseIndex] = useState<number | null>(null);
  const [editExpense, setEditExpense] = useState({ description: '', amount: '' });

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.budgeting) {
            setIncome(data.budgeting.income || []);
            setExpenses(data.budgeting.expenses || []);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentUser]);

  // Save data to Firebase
  const saveData = async (updatedIncome: BudgetItem[], updatedExpenses: BudgetItem[]) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        await setDoc(docRef, {
          ...docSnap.data(),
          budgeting: {
            income: updatedIncome,
            expenses: updatedExpenses,
            lastUpdated: new Date(),
          },
          lastUpdated: new Date(),
        });
      } else {
        await setDoc(docRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          createdAt: new Date(),
          lastUpdated: new Date(),
          budgeting: {
            income: updatedIncome,
            expenses: updatedExpenses,
            lastUpdated: new Date(),
          },
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  };

  const addIncome = () => {
    if (newIncome.description.trim() && newIncome.amount) {
      const updated = [
        ...income,
        {
          description: newIncome.description.trim(),
          amount: parseFloat(newIncome.amount),
        },
      ];
      setIncome(updated);
      setNewIncome({ description: '', amount: '' });
      saveData(updated, expenses);
    }
  };

  const addExpense = () => {
    if (newExpense.description.trim() && newExpense.amount) {
      const updated = [
        ...expenses,
        {
          description: newExpense.description.trim(),
          amount: parseFloat(newExpense.amount),
        },
      ];
      setExpenses(updated);
      setNewExpense({ description: '', amount: '' });
      saveData(income, updated);
    }
  };

  const removeIncome = (index: number) => {
    const updated = income.filter((_, i) => i !== index);
    setIncome(updated);
    saveData(updated, expenses);
  };

  const removeExpense = (index: number) => {
    const updated = expenses.filter((_, i) => i !== index);
    setExpenses(updated);
    saveData(income, updated);
  };

  // Edit income functions
  const startEditingIncome = (index: number) => {
    setEditingIncomeIndex(index);
    setEditIncome({
      description: income[index].description,
      amount: income[index].amount.toString(),
    });
  };

  const cancelEditingIncome = () => {
    setEditingIncomeIndex(null);
    setEditIncome({ description: '', amount: '' });
  };

  const saveIncomeEdit = () => {
    if (editingIncomeIndex !== null && editIncome.description.trim() && editIncome.amount) {
      const updated = [...income];
      updated[editingIncomeIndex] = {
        description: editIncome.description.trim(),
        amount: parseFloat(editIncome.amount),
      };
      setIncome(updated);
      saveData(updated, expenses);
      setEditingIncomeIndex(null);
      setEditIncome({ description: '', amount: '' });
    }
  };

  // Edit expense functions
  const startEditingExpense = (index: number) => {
    setEditingExpenseIndex(index);
    setEditExpense({
      description: expenses[index].description,
      amount: expenses[index].amount.toString(),
    });
  };

  const cancelEditingExpense = () => {
    setEditingExpenseIndex(null);
    setEditExpense({ description: '', amount: '' });
  };

  const saveExpenseEdit = () => {
    if (editingExpenseIndex !== null && editExpense.description.trim() && editExpense.amount) {
      const updated = [...expenses];
      updated[editingExpenseIndex] = {
        description: editExpense.description.trim(),
        amount: parseFloat(editExpense.amount),
      };
      setExpenses(updated);
      saveData(income, updated);
      setEditingExpenseIndex(null);
      setEditExpense({ description: '', amount: '' });
    }
  };

  const totalIncome = income.reduce((sum, item) => sum + item.amount, 0);
  const totalExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);
  const netIncome = totalIncome - totalExpenses;

  return (
    <SideHustleLayout currentPage="budgeting">
      <div className="flex flex-col items-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-5xl w-full">
          <h1 className="text-5xl font-bold text-center mb-4">
            Business Budgeting & Planning
          </h1>
          <p className="text-2xl text-center text-gray-600 mb-12">
            Cashflow is key!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Income Section */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-center">Income</h2>

              <div className="bg-primary p-6 rounded-3xl shadow-lg mb-6">
                <div className="mb-4">
                  <h3 className="text-white font-bold text-xl mb-4">
                    Total Income: ${totalIncome.toFixed(2)}
                  </h3>

                  <div className="space-y-2 mb-4">
                    {income.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-white"
                      >
                        {editingIncomeIndex === index ? (
                          <div className="flex-1 flex gap-2">
                            <input
                              type="text"
                              value={editIncome.description}
                              onChange={(e) =>
                                setEditIncome({ ...editIncome, description: e.target.value })
                              }
                              placeholder="Description"
                              className="flex-1 px-3 py-1 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
                              autoFocus
                            />
                            <input
                              type="number"
                              value={editIncome.amount}
                              onChange={(e) =>
                                setEditIncome({ ...editIncome, amount: e.target.value })
                              }
                              placeholder="Amount"
                              className="w-24 px-3 py-1 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
                            />
                            <button
                              onClick={saveIncomeEdit}
                              className="bg-white text-primary px-3 py-1 rounded-lg hover:bg-gray-100 font-semibold text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditingIncome}
                              className="bg-gray-400 text-white px-3 py-1 rounded-lg hover:bg-gray-500 font-semibold text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <span>• {item.description}: ${item.amount.toFixed(2)}</span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => startEditingIncome(index)}
                                className="text-white hover:text-gray-200 font-semibold text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => removeIncome(index)}
                                className="text-white hover:text-red-200 font-bold"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <input
                    type="text"
                    value={newIncome.description}
                    onChange={(e) =>
                      setNewIncome({ ...newIncome, description: e.target.value })
                    }
                    placeholder="Income source"
                    className="w-full px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-white"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={newIncome.amount}
                      onChange={(e) =>
                        setNewIncome({ ...newIncome, amount: e.target.value })
                      }
                      placeholder="Amount"
                      className="flex-1 px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-white"
                    />
                    <button
                      onClick={addIncome}
                      className="bg-white text-primary px-6 py-2 rounded-xl hover:bg-gray-100 transition-all font-semibold"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses Section */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-center">Expenses</h2>

              <div className="bg-red-500 p-6 rounded-3xl shadow-lg mb-6">
                <div className="mb-4">
                  <h3 className="text-white font-bold text-xl mb-4">
                    Total Expenses: ${totalExpenses.toFixed(2)}
                  </h3>

                  <div className="space-y-2 mb-4">
                    {expenses.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-white"
                      >
                        {editingExpenseIndex === index ? (
                          <div className="flex-1 flex gap-2">
                            <input
                              type="text"
                              value={editExpense.description}
                              onChange={(e) =>
                                setEditExpense({ ...editExpense, description: e.target.value })
                              }
                              placeholder="Description"
                              className="flex-1 px-3 py-1 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
                              autoFocus
                            />
                            <input
                              type="number"
                              value={editExpense.amount}
                              onChange={(e) =>
                                setEditExpense({ ...editExpense, amount: e.target.value })
                              }
                              placeholder="Amount"
                              className="w-24 px-3 py-1 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
                            />
                            <button
                              onClick={saveExpenseEdit}
                              className="bg-white text-red-500 px-3 py-1 rounded-lg hover:bg-gray-100 font-semibold text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditingExpense}
                              className="bg-gray-400 text-white px-3 py-1 rounded-lg hover:bg-gray-500 font-semibold text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <span>• {item.description}: ${item.amount.toFixed(2)}</span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => startEditingExpense(index)}
                                className="text-white hover:text-gray-200 font-semibold text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => removeExpense(index)}
                                className="text-white hover:text-red-200 font-bold"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <input
                    type="text"
                    value={newExpense.description}
                    onChange={(e) =>
                      setNewExpense({ ...newExpense, description: e.target.value })
                    }
                    placeholder="Expense item"
                    className="w-full px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-white"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={newExpense.amount}
                      onChange={(e) =>
                        setNewExpense({ ...newExpense, amount: e.target.value })
                      }
                      placeholder="Amount"
                      className="flex-1 px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-white"
                    />
                    <button
                      onClick={addExpense}
                      className="bg-white text-red-500 px-6 py-2 rounded-xl hover:bg-gray-100 transition-all font-semibold"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Net Income Summary */}
          <div className="mt-8 text-center">
            <div
              className={`inline-block px-8 py-4 rounded-3xl shadow-lg ${
                netIncome >= 0 ? 'bg-primary' : 'bg-red-500'
              } text-white`}
            >
              <h3 className="text-2xl font-bold">
                Net Income: ${netIncome.toFixed(2)}
              </h3>
              <p className="text-white/90">
                {netIncome >= 0
                  ? "You're in the green! Keep it up!"
                  : 'Your expenses exceed your income. Time to adjust!'}
              </p>
            </div>
          </div>

          {saving && (
            <div className="text-center mt-6 text-primary font-semibold">
              Saving...
            </div>
          )}
        </div>
      </div>
    </SideHustleLayout>
  );
};

export default BusinessBudgeting;
